import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonIcon from "@mui/icons-material/PersonOutline";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOrgStore } from "@stores/orgStore";
import { useNavigate } from "@tanstack/react-router";
import { type MouseEvent, useMemo, useState } from "react";
import { useOrgs } from "../hooks/useOrgs";

/**
 * Compact org-context switcher for the sidebar.
 *
 * Shows the active context as a pill ("Personal" or the org name) with a
 * dropdown caret. Click opens a menu with:
 *   - Personal option
 *   - All orgs the user is a member of
 *   - "Manage organisations" link to the settings page
 *
 * Phase 1a: selecting an org is UI-only (the selection persists via
 * orgStore but doesn't yet filter projects / chats — that's Phase 2).
 */
export function OrgSwitcher() {
  const { data: orgs } = useOrgs();
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const setActiveOrg = useOrgStore((s) => s.setActiveOrg);
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const activeOrg = useMemo(
    () =>
      activeOrgId ? (orgs?.find((o) => o.id === activeOrgId) ?? null) : null,
    [orgs, activeOrgId],
  );

  const open = Boolean(anchor);
  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  const select = (id: string | null) => {
    setActiveOrg(id);
    handleClose();
  };

  const displayName = activeOrg ? activeOrg.name : "Personal";
  const DisplayIcon = activeOrg ? BusinessIcon : PersonIcon;

  return (
    <Box>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={(t) => ({
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          border: "1px solid",
          borderColor: open
            ? alpha(t.palette.primary.main, 0.45)
            : t.palette.border.default,
          bgcolor: open ? alpha(t.palette.primary.main, 0.06) : "surface.raised",
          cursor: "pointer",
          position: "relative",
          transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            borderRadius: "inherit",
            boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, open ? 0.18 : 0.04)}`,
            transition: `box-shadow ${t.motion.fast}`,
          },
          "&:hover": {
            borderColor: alpha(t.palette.primary.main, 0.3),
            bgcolor: alpha(t.palette.primary.main, 0.05),
          },
          "&:hover::before": {
            boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.16)}`,
          },
          "&:focus-visible": {
            outline: `2px solid ${alpha(t.palette.primary.main, 0.4)}`,
            outlineOffset: -2,
          },
        })}
      >
        <DisplayIcon sx={{ fontSize: 16, color: "primary.light" }} />
        <Typography
          sx={{
            flex: 1,
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
          }}
        >
          {displayName}
        </Typography>
        <IconButton
          size="small"
          tabIndex={-1}
          sx={{ p: "2px", color: "text.secondary" }}
          aria-label="Open org switcher"
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              transform: open ? "rotate(180deg)" : "rotate(0)",
              transition: (t) => `transform ${t.motion.fast}`,
            }}
          />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: (t) => ({
              // Compact, sidebar-sized dropdown — explicit width keeps
              // the menu from auto-growing to fit long org names.
              width: 260,
              maxWidth: "calc(100vw - 16px)",
              mt: 0.5,
              borderRadius: 1.25,
              bgcolor: "surface.raised",
              border: "1px solid",
              borderColor: "border.default",
              overflow: "hidden",
              position: "relative",
              // Hairline trace-cyan rim — same instrument-panel framing
              // as the create dialog and project cards.
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                borderRadius: "inherit",
                boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.12)}, 0 0 24px -10px ${alpha(t.palette.primary.main, 0.4)}`,
              },
              "& .MuiList-root": {
                py: 0.5,
              },
            }),
          },
        }}
      >
        <ContextSectionLabel label="Switch context" />

        <EditorialMenuItem
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          primary="Personal"
          selected={activeOrgId === null}
          onClick={() => select(null)}
        />

        {orgs && orgs.length > 0 && (
          <Divider
            sx={(t) => ({
              my: 0.5,
              borderColor: alpha(t.palette.primary.main, 0.12),
            })}
          />
        )}

        {orgs?.map((org) => (
          <EditorialMenuItem
            key={org.id}
            icon={<BusinessIcon sx={{ fontSize: 16 }} />}
            primary={org.name}
            secondary={`${org.member_count} member${
              org.member_count === 1 ? "" : "s"
            } · ${org.my_role}`}
            selected={activeOrgId === org.id}
            onClick={() => select(org.id)}
          />
        ))}

        <Divider
          sx={(t) => ({
            my: 0.5,
            borderColor: alpha(t.palette.primary.main, 0.12),
          })}
        />

        <EditorialMenuItem
          icon={<SettingsIcon sx={{ fontSize: 16 }} />}
          primary="Manage organisations"
          onClick={() => {
            handleClose();
            void navigate({ to: "/settings/organisations" });
          }}
        />
      </Menu>
    </Box>
  );
}

/** Mono ALL-CAPS section header inside the switcher menu — same kicker
 *  vocabulary as the sidebar clusters and dialog eyebrows. */
function ContextSectionLabel({ label }: { label: string }) {
  return (
    <Typography
      sx={{
        fontFamily: (t) => t.fonts.mono,
        fontSize: "0.625rem",
        fontWeight: 500,
        color: "text.secondary",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        px: 1.5,
        py: 0.75,
      }}
    >
      {label}
    </Typography>
  );
}

interface EditorialMenuItemProps {
  icon: React.ReactNode;
  primary: string;
  secondary?: string;
  selected?: boolean;
  onClick: () => void;
}

/**
 * Menu item that wears the editorial vocabulary. Selected rows pick up
 * the trace-cyan inset rim + soft tint that mirrors the project card
 * active state and the SidebarNav selected treatment, instead of MUI's
 * default `Mui-selected` background. The check icon on the right edge
 * stands in for the previous `<CheckIcon />` — same intent, smaller
 * footprint, integrated with the rim.
 */
function EditorialMenuItem({
  icon,
  primary,
  secondary,
  selected,
  onClick,
}: EditorialMenuItemProps) {
  return (
    <MenuItem
      onClick={onClick}
      disableRipple
      sx={(t) => ({
        mx: 0.5,
        my: 0.25,
        px: 1.25,
        py: 0.875,
        borderRadius: 0.875,
        position: "relative",
        overflow: "hidden",
        bgcolor: selected ? alpha(t.palette.primary.main, 0.1) : "transparent",
        color: selected ? "primary.light" : "text.primary",
        transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          borderRadius: "inherit",
          boxShadow: selected
            ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.42)}`
            : "none",
          transition: `box-shadow ${t.motion.fast}`,
        },
        "&:hover": {
          bgcolor: alpha(t.palette.primary.main, selected ? 0.16 : 0.06),
          color: selected ? "primary.light" : "text.primary",
        },
        "&:hover::before": {
          boxShadow: selected
            ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}`
            : `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`,
        },
        "&.Mui-selected, &.Mui-selected:hover": {
          bgcolor: "inherit",
        },
      })}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: 1,
          color: selected ? "primary.light" : "text.secondary",
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={primary}
        secondary={secondary}
        sx={{ minWidth: 0 }}
        primaryTypographyProps={{
          fontSize: "0.8125rem",
          fontWeight: selected ? 600 : 500,
          letterSpacing: "-0.005em",
          noWrap: true,
        }}
        secondaryTypographyProps={{
          noWrap: true,
          sx: {
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.65rem",
            letterSpacing: "0.04em",
            color: "text.secondary",
            mt: 0.125,
          },
        }}
      />
      {selected && (
        <Box
          aria-hidden
          sx={(t) => ({
            width: 5,
            height: 5,
            borderRadius: "50%",
            bgcolor: "primary.main",
            boxShadow: `0 0 0 2px ${alpha(t.palette.primary.main, 0.18)}`,
            ml: 1,
            flexShrink: 0,
          })}
        />
      )}
    </MenuItem>
  );
}
